import React, { useEffect } from 'react';

import './IntroPage.scss';
import { Stack } from '@mui/material';
import { GHOST_WHITE_HEX } from '../../theme';
import OnboardingFlow from './onboarding/OnboardingFlow';
import { WebViewDetector, WebViewInfo } from '../../services/webViewDetector/webViewDetector';
import { LandingPageFlavorEnum } from './onboarding/Welcome/welcomeFlavors.types';
import OnboardBeforeSignInFlow from './onboarding/OnboardBeforeSignInFlow';
import { openAppStoreLink } from '../../utils/appStoreUtils';
import { useAppSelector } from '../../app/hooks';
import { selectWebViewInfo } from '../../features/ui/uiSlice';
import { useHardAnnualOnlyTrialPaywall } from '../../hooks/useHardAnnualOnlyTrialPaywall';
import GatedOnboardingFlow from './onboarding/GatedOnboardingFlow';
import OnboardingFlowWithSlideshow from './onboarding/OnboardingFlowWithSlideshow';
import FyxerOnboardingFlow from './onboarding/FyxerOnboardingFlow';

export type IntroFunnelMode = 'web2app' | 'fyxer' | 'gated';

export type IntroPageProps = {
  landingPageFlavor: LandingPageFlavorEnum;
  funnelMode?: IntroFunnelMode;
};

function renderOnboardingFlow(
  landingPageFlavor: LandingPageFlavorEnum,
  webViewInfo: WebViewInfo | undefined,
  funnelMode: IntroFunnelMode | undefined,
  isHardAnnualOnlyTrialPaywallNoSlideshow: boolean,
  isHardAnnualOnlyTrialPaywallWithSlideshow: boolean
) {
  if (funnelMode === 'fyxer') {
    return <FyxerOnboardingFlow />;
  }

  if (funnelMode === 'gated') {
    return <GatedOnboardingFlow />;
  }

  if (webViewInfo?.isInApp || funnelMode === 'web2app') {
    return (
      <OnboardBeforeSignInFlow
        landingPageFlavor={LandingPageFlavorEnum.CLEAN_EMAIL_PUNCHY}
        nativeAppFunnelMode={true}
      />
    );
  }

  if (isHardAnnualOnlyTrialPaywallNoSlideshow || isHardAnnualOnlyTrialPaywallWithSlideshow) {
    return <OnboardingFlowWithSlideshow landingPageFlavor={landingPageFlavor} />;
  }

  return <OnboardingFlow landingPageFlavor={landingPageFlavor} />;
}

function IntroPage(props: IntroPageProps) {
  const vh = window.innerHeight * 0.01;

  const { landingPageFlavor, funnelMode } = props;

  // Set the CSS variable to the root element
  // Which is equal to 1vh
  document.documentElement.style.setProperty('--vh', vh + 'px');

  useEffect(() => {
    const webViewInfo = WebViewDetector.checkForWebView();
    if (webViewInfo?.isInApp && !funnelMode) {
      const appStoreLink = webViewInfo?.appStoreLink;
      if (appStoreLink) {
        openAppStoreLink(appStoreLink);
      }
    }
  }, [funnelMode]);

  const webViewInfo = useAppSelector(selectWebViewInfo);

  const { isHardAnnualOnlyTrialPaywallNoSlideshow, isHardAnnualOnlyTrialPaywallWithSlideshow } =
    useHardAnnualOnlyTrialPaywall();

  return (
    <Stack
      direction='column'
      alignItems='center'
      className='full-height-stack blue-gradient'
      sx={{
        backgroundColor: GHOST_WHITE_HEX,
      }}
    >
      {renderOnboardingFlow(
        landingPageFlavor,
        webViewInfo,
        funnelMode,
        isHardAnnualOnlyTrialPaywallNoSlideshow,
        isHardAnnualOnlyTrialPaywallWithSlideshow
      )}
    </Stack>
  );
}
export default IntroPage;
